import React from "react";

import { faPlus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Button from "ui/Button";
import DocsSnippet from "ui/DocsSnippet";
import Panel from "ui/Panel";

import { WithCurrentOrganizationQuery } from "providers/WithCurrentOrganization/gql/Query.generated";

import GroupPicker, { type SelectableGroup } from "./GroupPicker";
import ServerPicker, {
  type SelectableServer,
  type ServerStatus,
} from "./ServerPicker";

type OrganizationType =
  WithCurrentOrganizationQuery["getCurrentUserDetails"]["organizations"][number];

const serverStatus = (
  latestStats: {
    isPrimary: boolean;
  } | null,
): ServerStatus => {
  if (latestStats == null) {
    return "no data";
  }
  return latestStats.isPrimary ? "primary" : "replica";
};

// Every server the org can see, with its primary/replica status, straight from the nav query.
// servers(standalone: true) leaves out members of a cluster that has a server group, and
// serverGroups covers those, so together the two lists are the whole fleet. A server can sit
// in more than one group, so dedupe by id to keep each one a single grid row.
export const selectableServers = (
  organization: OrganizationType,
): SelectableServer[] => {
  const byId = new Map<string, SelectableServer>();
  for (const server of [
    ...organization.servers,
    ...organization.serverGroups.flatMap((group) => group.servers),
  ]) {
    byId.set(server.id, {
      id: server.id,
      name: server.name,
      clusterId: server.clusterId,
      status: serverStatus(server.latestStats),
      hasRecentSnapshot: server.hasRecentSnapshot,
    });
  }
  return Array.from(byId.values());
};

export type MatchRule = {
  type: "tag" | "server_name" | "cluster_id" | "role";
  key: string;
  value: string;
};

export const emptyTagRule = (): MatchRule => ({
  type: "tag",
  key: "",
  value: "",
});

export const ruleRequiresKey = (rule: MatchRule): boolean =>
  rule.type === "tag";

const valueForTypeChange = (
  prevType: MatchRule["type"],
  nextType: MatchRule["type"],
): Partial<MatchRule> => {
  if (nextType === "role") {
    return { value: "primary" };
  }
  if (prevType === "role") {
    return { value: "" };
  }
  return {};
};

const VALUE_LABELS: Record<MatchRule["type"], string> = {
  tag: "Value",
  server_name: "Name",
  cluster_id: "Cluster ID",
  role: "Role",
};

// Candidates for nested membership picker. A group that already includes groups of its own cannot
// also be included, since ServerGroupMembership caps nesting at depth 1, and a group can never include itself.
export const includableGroups = <
  T extends { id: string; memberServerGroupIds: string[] },
>(
  groups: T[],
  serverGroupId?: string,
): T[] =>
  groups.filter(
    (group) =>
      group.id !== serverGroupId && group.memberServerGroupIds.length === 0,
  );

// Servers the candidate rules already match. They are members through the rule either way, so the
// picker locks them and the save payload leaves them out.
export const ruleMatchedIds = (
  members: readonly { id: string; matchedByRule: boolean }[] | undefined,
): string[] =>
  (members ?? [])
    .filter((member) => member.matchedByRule)
    .map((member) => member.id);

export const enteredRules = (rules: MatchRule[]): MatchRule[] =>
  rules.filter((rule) => !(rule.key.trim() === "" && rule.value.trim() === ""));

// A rule the user started but did not finish. Checked only against enteredRules.
export const isIncompleteRule = (rule: MatchRule): boolean =>
  (ruleRequiresKey(rule) && rule.key.trim() === "") || rule.value.trim() === "";

export const validationError = ({
  name,
  matchRules,
  memberServerIds,
  memberServerGroupIds,
}: {
  name: string;
  matchRules: MatchRule[];
  memberServerIds: string[];
  memberServerGroupIds: string[];
}): string | null => {
  if (name.trim() === "") {
    return "Name is required.";
  }
  const rules = enteredRules(matchRules);
  if (rules.some(isIncompleteRule)) {
    return "All match rule fields are required.";
  }
  if (
    rules.length === 0 &&
    memberServerIds.length === 0 &&
    memberServerGroupIds.length === 0
  ) {
    return "Add at least one match rule or member.";
  }
  return null;
};

export const toMatchRuleInput = (rules: MatchRule[]) =>
  rules.map((rule) => ({
    matchType: rule.type,
    matchKey: ruleRequiresKey(rule) ? rule.key.trim() : null,
    pattern: rule.value.trim(),
  }));

type Props = {
  name: string;
  description: string;
  matchRules: MatchRule[];
  servers: SelectableServer[];
  serverGroups: SelectableGroup[];
  memberServerIds: string[];
  memberServerGroupIds: string[];
  ruleMatchedServerIds: string[];
  canIncludeServerGroups?: boolean;
  disabled?: boolean;
  footer?: React.ReactNode;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onMatchRulesChange: (matchRules: MatchRule[]) => void;
  onMemberServerIdsChange: (ids: string[]) => void;
  onMemberServerGroupIdsChange: (ids: string[]) => void;
};

const ServerGroupForm = ({
  name,
  description,
  matchRules,
  servers,
  serverGroups,
  memberServerIds,
  memberServerGroupIds,
  ruleMatchedServerIds,
  canIncludeServerGroups = true,
  disabled,
  footer,
  onNameChange,
  onDescriptionChange,
  onMatchRulesChange,
  onMemberServerIdsChange,
  onMemberServerGroupIdsChange,
}: Props) => {
  const updateRule = (index: number, updates: Partial<MatchRule>) => {
    onMatchRulesChange(
      matchRules.map((rule, i) =>
        i === index ? { ...rule, ...updates } : rule,
      ),
    );
  };

  const addRule = () => {
    onMatchRulesChange([...matchRules, emptyTagRule()]);
  };

  const removeRule = (index: number) => {
    onMatchRulesChange(matchRules.filter((_, i) => i !== index));
  };

  return (
    <>
      <Panel title="Server Group Details">
        <div className="p-[10px]">
          <div className="form-group">
            <label className="control-label" htmlFor="server_group_name">
              Name <span className="text-[#c00]">*</span>
            </label>
            <input
              id="server_group_name"
              className="form-control max-w-[700px]"
              type="text"
              value={name}
              disabled={disabled}
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="control-label" htmlFor="server_group_description">
              Description
            </label>
            <textarea
              id="server_group_description"
              className="form-control resize-y max-w-[700px]"
              rows={3}
              value={description}
              disabled={disabled}
              onChange={(e) => onDescriptionChange(e.target.value)}
            />
          </div>
        </div>
      </Panel>
      <Panel
        title="Match servers automatically"
        secondaryTitle="All rules must match"
      >
        <div className="p-[10px]">
          {matchRules.map((rule, i) => (
            <div key={i} className="mb-2">
              <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_24px] gap-x-2 pb-1 font-bold">
                <div>Type</div>
                {ruleRequiresKey(rule) ? (
                  <>
                    <div>Key</div>
                    <div>{VALUE_LABELS[rule.type]}</div>
                  </>
                ) : (
                  <div className="col-span-2">
                    {VALUE_LABELS[rule.type]}
                    {rule.type === "server_name" && (
                      <>
                        {" "}
                        <DocsSnippet
                          title="Server Name"
                          content="Use * as a wildcard to match by name."
                        />
                      </>
                    )}
                    {rule.type === "cluster_id" && (
                      <>
                        {" "}
                        <DocsSnippet
                          title="Cluster ID"
                          content="Use * as a wildcard to match by cluster."
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_24px] gap-x-2 items-center">
                <select
                  className="form-control"
                  aria-label="Type"
                  value={rule.type}
                  disabled={disabled}
                  onChange={(e) => {
                    const type = e.target.value as MatchRule["type"];
                    updateRule(i, {
                      type,
                      ...valueForTypeChange(rule.type, type),
                    });
                  }}
                >
                  <option value="tag">Tag</option>
                  <option value="server_name">Server name</option>
                  <option value="cluster_id">Cluster ID</option>
                  <option value="role">Role</option>
                </select>
                {ruleRequiresKey(rule) && (
                  <input
                    className="form-control"
                    type="text"
                    required
                    placeholder="e.g. environment"
                    value={rule.key}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { key: e.target.value })}
                  />
                )}
                {rule.type === "role" ? (
                  <select
                    className="form-control col-span-2"
                    aria-label="Role"
                    value={rule.value}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                  >
                    <option value="primary">Primary</option>
                    <option value="replica">Replica</option>
                  </select>
                ) : (
                  <input
                    className={`form-control ${
                      ruleRequiresKey(rule) ? "" : "col-span-2"
                    }`}
                    type="text"
                    required
                    placeholder="use * to match anything, e.g. prod-*"
                    value={rule.value}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                  />
                )}
                <Button
                  bare
                  title="Remove"
                  className="text-[#999] hover:text-[#333] cursor-pointer"
                  disabled={disabled}
                  onClick={() => removeRule(i)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            </div>
          ))}
          <Button
            bare
            className="btn btn-primary"
            disabled={disabled}
            onClick={addRule}
          >
            <FontAwesomeIcon icon={faPlus} className="text-[10px] mr-[4px]" />
            Add rule
          </Button>
        </div>
      </Panel>
      <Panel
        title="Pick servers directly"
        secondaryTitle="Added on top of the rules above"
      >
        <div className="p-[10px]">
          <ServerPicker
            servers={servers}
            selectedIds={memberServerIds}
            onChange={onMemberServerIdsChange}
            ruleMatchedIds={ruleMatchedServerIds}
            disabled={disabled}
          />
        </div>
      </Panel>
      <Panel
        title="Include other groups"
        secondaryTitle={
          canIncludeServerGroups ? "Their members are included" : undefined
        }
      >
        <div className="p-[10px]">
          {canIncludeServerGroups ? (
            <GroupPicker
              groups={serverGroups}
              selectedIds={memberServerGroupIds}
              onChange={onMemberServerGroupIdsChange}
              disabled={disabled}
            />
          ) : (
            <div className="text-[#606060]">
              This group is included in another group, so it cannot include
              groups of its own.
            </div>
          )}
        </div>
      </Panel>
      {footer}
    </>
  );
};

export default ServerGroupForm;
